import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { track } from '../analytics/track';
import { affiliateFromSearch, forgetAffiliate, pendingAffiliate, rememberAffiliate } from './affiliate';

/**
 * Keeps the affiliate code a link carried (?via=<code>), counts the link's first visit
 * from this browser, and once there is an account tells the API, once, whose link it
 * was. Mounted for the whole app: an affiliate may link to any page.
 */
export default function AffiliateClaim() {
  const { isAuthenticated } = useAuth();
  const { search } = useLocation();
  const asked = useRef(false);

  useEffect(() => {
    const fromLink = affiliateFromSearch(search);
    // Counted once per browser: a code already kept is not a new visit
    if (fromLink && rememberAffiliate(fromLink)) {
      api.recordAffiliateVisit(fromLink).catch(() => {});
    }
    if (!isAuthenticated || asked.current) return;
    const pending = pendingAffiliate();
    if (!pending) return;
    asked.current = true;
    api.claimAffiliate(pending.code, new Date(pending.firstSeenAt).toISOString())
      .then((result) => {
        // Accepted or refused, the API has answered: asking again changes nothing
        forgetAffiliate();
        track(result?.accepted ? 'affiliate_claim_accepted' : 'affiliate_claim_not_accepted', { reason: result?.reason });
      })
      .catch(() => {
        // The network, the API mid-deploy, or Stripe unreachable (503): ask on the next load
        asked.current = false;
      });
  }, [isAuthenticated, search]);

  return null;
}
